// src/pages/Products/Products.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Image, Spinner, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaBox, FaImages, FaUpload, FaTimes, FaTags, FaStar, FaDollarSign, FaWeightHanging, FaBoxOpen } from 'react-icons/fa';
import { productsAPI, vendorsAPI, categoriesAPI, subcategoriesAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
    const [vendorsLoading, setVendorsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Photo management states
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [productPhotos, setProductPhotos] = useState([]);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);
    const [deletingPhotoId, setDeletingPhotoId] = useState(null);
    const [newPhotos, setNewPhotos] = useState([]);
    const [settingPrimary, setSettingPrimary] = useState(null);
    const [photosLoading, setPhotosLoading] = useState(false);

    // Variant management states
    const [showVariantModal, setShowVariantModal] = useState(false);
    const [productVariants, setProductVariants] = useState([]);
    const [variantsLoading, setVariantsLoading] = useState(false);
    const [showCreateVariantModal, setShowCreateVariantModal] = useState(false);
    const [showEditVariantModal, setShowEditVariantModal] = useState(false);
    const [showDeleteVariantModal, setShowDeleteVariantModal] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [variantFormData, setVariantFormData] = useState({
        sku: '',
        title: '',
        price: '',
        compare_price: '',
        weight: ''
    });

    // Form data
    const [formData, setFormData] = useState({
        vendor_id: '',
        sku: '',
        name: '',
        description: '',
        category_id: '',
        subcategory_id: '',
        is_active: '1'
    });
    const [photos, setPhotos] = useState([]);

    const API_BASE_URL = import.meta.env.VITE_API_URL;

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchVendors();
    }, []);

    useEffect(() => {
        const filtered = products.filter(product =>
            product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.vendor_name && product.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (product.category_name && product.category_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (product.subcategory_name && product.subcategory_name.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        setFilteredProducts(filtered);
    }, [searchTerm, products]);

    // Fetch subcategories when category_id changes
    useEffect(() => {
        if (formData.category_id) {
            fetchSubcategories(formData.category_id);
        } else {
            setSubcategories([]);
            setFormData(prev => ({ ...prev, subcategory_id: '' }));
        }
    }, [formData.category_id]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await productsAPI.list({
                page: 1,
                limit: 50
            });

            console.log('Products API Response:', response.data);

            // Handle the response structure from your API
            let productsData = [];
            if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
                productsData = response.data.data.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                productsData = response.data.data;
            } else if (response.data && Array.isArray(response.data)) {
                productsData = response.data;
            }

            setProducts(productsData || []);
            setFilteredProducts(productsData || []);

            if (!productsData || productsData.length === 0) {
                console.log('No products found in response');
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch products';
            setError(errorMessage);
            setProducts([]);
            setFilteredProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            setCategoriesLoading(true);
            const response = await categoriesAPI.list({
                page: 1,
                limit: 50
            });

            console.log('Categories API Response:', response.data);

            let categoriesData = [];
            if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
                categoriesData = response.data.data.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                categoriesData = response.data.data;
            } else if (response.data && Array.isArray(response.data)) {
                categoriesData = response.data;
            }

            setCategories(categoriesData || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load categories: ' + (err.response?.data?.message || err.message));
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            setVendorsLoading(true);
            const response = await vendorsAPI.list({
                page: 1,
                limit: 50
            });

            console.log('Vendors API Response:', response.data);

            let vendorsData = [];
            if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
                vendorsData = response.data.data.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                vendorsData = response.data.data;
            } else if (response.data && Array.isArray(response.data)) {
                vendorsData = response.data;
            }

            setVendors(vendorsData || []);
        } catch (err) {
            console.error('Error fetching vendors:', err);
            setError('Failed to load vendors: ' + (err.response?.data?.message || err.message));
            setVendors([]);
        } finally {
            setVendorsLoading(false);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            setSubcategoriesLoading(true);
            const response = await subcategoriesAPI.list({
                category_id: categoryId,
                page: 1,
                limit: 50
            });

            console.log('Subcategories API Response:', response.data);

            let subcategoriesData = [];
            if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
                subcategoriesData = response.data.data.data;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                subcategoriesData = response.data.data;
            } else if (response.data && Array.isArray(response.data)) {
                subcategoriesData = response.data;
            }

            setSubcategories(subcategoriesData || []);
        } catch (err) {
            console.error('Error fetching subcategories:', err);
            setSubcategories([]);
        } finally {
            setSubcategoriesLoading(false);
        }
    };

    // Variant Management Functions
    const fetchProductVariants = async (productId) => {
        try {
            console.log('Fetching variants for product:', productId);
            setVariantsLoading(true);

            const response = await productsAPI.getVariants(productId);

            console.log('Variants API response:', response.data);

            let variantsData = [];
            if (response.data && response.data.data && response.data.data.variants && Array.isArray(response.data.data.variants)) {
                variantsData = response.data.data.variants;
            } else if (response.data && response.data.variants && Array.isArray(response.data.variants)) {
                variantsData = response.data.variants;
            } else if (response.data && Array.isArray(response.data)) {
                variantsData = response.data;
            }

            console.log('Raw variants data:', variantsData);
            setProductVariants(variantsData || []);

        } catch (err) {
            console.error('Error fetching product variants:', err);
            console.error('Error details:', err.response?.data || err.message);
            setProductVariants([]);
            setError('Failed to load product variants: ' + (err.response?.data?.message || err.message || 'Unknown error'));
        } finally {
            setVariantsLoading(false);
        }
    };

    const openVariantModal = async (product) => {
        console.log('Opening variant modal for product:', product);
        setSelectedProduct(product);
        setShowVariantModal(true);
        setProductVariants([]);
        await fetchProductVariants(product.id);
    };

    const openCreateVariantModal = () => {
        setVariantFormData({
            sku: selectedProduct.sku || '',
            title: '',
            price: '',
            compare_price: '',
            weight: ''
        });
        setShowCreateVariantModal(true);
    };

    const openEditVariantModal = (variant) => {
        setSelectedVariant(variant);
        setVariantFormData({
            sku: variant.sku || '',
            title: variant.title || '',
            price: variant.price || '',
            compare_price: variant.compare_price || '',
            weight: variant.weight || ''
        });
        setShowEditVariantModal(true);
    };

    const openDeleteVariantModal = (variant) => {
        setSelectedVariant(variant);
        setShowDeleteVariantModal(true);
    };

    const handleCreateVariant = async (e) => {
        e.preventDefault();
        try {
            setError('');

            const variantData = {
                sku: variantFormData.sku,
                title: variantFormData.title,
                price: parseFloat(variantFormData.price) || 0,
                compare_price: variantFormData.compare_price ? parseFloat(variantFormData.compare_price) : null,
                weight: variantFormData.weight ? parseFloat(variantFormData.weight) : null
            };

            console.log('Creating variant with data:', variantData);

            await productsAPI.createVariant(selectedProduct.id, variantData);

            setShowCreateVariantModal(false);
            setVariantFormData({
                sku: '',
                title: '',
                price: '',
                compare_price: '',
                weight: ''
            });
            setSuccess('Variant created successfully!');

            // Refresh variants list
            await fetchProductVariants(selectedProduct.id);
        } catch (err) {
            console.error('Error creating variant:', err);
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to create variant';
            setError(errorMessage);
        }
    };

    const handleUpdateVariant = async (e) => {
        e.preventDefault();
        try {
            setError('');

            const variantData = {
                sku: variantFormData.sku,
                title: variantFormData.title,
                price: parseFloat(variantFormData.price) || 0,
                compare_price: variantFormData.compare_price ? parseFloat(variantFormData.compare_price) : null,
                weight: variantFormData.weight ? parseFloat(variantFormData.weight) : null
            };

            console.log('Updating variant with data:', variantData);

            await productsAPI.updateVariant(selectedProduct.id, selectedVariant.id, variantData);

            setShowEditVariantModal(false);
            setSelectedVariant(null);
            setVariantFormData({
                sku: '',
                title: '',
                price: '',
                compare_price: '',
                weight: ''
            });
            setSuccess('Variant updated successfully!');

            // Refresh variants list
            await fetchProductVariants(selectedProduct.id);
        } catch (err) {
            console.error('Error updating variant:', err);
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to update variant';
            setError(errorMessage);
        }
    };

    const handleDeleteVariant = async () => {
        try {
            setError('');
            await productsAPI.deleteVariant(selectedProduct.id, selectedVariant.id);

            setShowDeleteVariantModal(false);
            setSelectedVariant(null);
            setSuccess('Variant deleted successfully!');

            // Refresh variants list
            await fetchProductVariants(selectedProduct.id);
        } catch (err) {
            console.error('Error deleting variant:', err);
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to delete variant';
            setError(errorMessage);
        }
    };

    const handleVariantFormChange = (e) => {
        const { name, value } = e.target;
        setVariantFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Photo Management Functions
    const fetchProductPhotos = async (productId) => {
        try {
            console.log('Fetching photos for product:', productId);
            setPhotosLoading(true);

            const response = await productsAPI.getPhotos(productId);

            console.log('Photos API response:', response.data);

            let photosData = [];
            if (response.data && response.data.data && response.data.data.photos && Array.isArray(response.data.data.photos)) {
                photosData = response.data.data.photos;
            } else if (response.data && response.data.photos && Array.isArray(response.data.photos)) {
                photosData = response.data.photos;
            } else if (response.data && Array.isArray(response.data)) {
                photosData = response.data;
            }

            console.log('Raw photos data:', photosData);

            // Process photos to ensure they have full URLs
            const processedPhotos = (photosData || []).map(photo => ({
                ...photo,
                id: photo.id,
                // Construct full URL for the photo - Based on your API response, URLs are stored without uploads/ prefix
                full_url: photo.url ? `${API_BASE_URL}/public/uploads/product_photos/${photo.url}` : '',
                thumbnail_url: photo.thumbnail_url || '',
                alt_text: photo.alt_text || `Product photo`,
                position: photo.position || 0,
                is_primary: photo.position === 0 || false
            }));

            console.log('Processed photos:', processedPhotos);
            setProductPhotos(processedPhotos);

        } catch (err) {
            console.error('Error fetching product photos:', err);
            console.error('Error details:', err.response?.data || err.message);
            // Check if the product in the list has primary_photo
            const product = products.find(p => p.id === productId);
            if (product && product.primary_photo) {
                const primaryPhoto = {
                    ...product.primary_photo,
                    id: product.primary_photo.id || 'primary',
                    full_url: product.primary_photo.url ? `${API_BASE_URL}/public/uploads/product_photos/${product.primary_photo.url}` : '',
                    alt_text: product.primary_photo.alt_text || 'Primary photo',
                    position: 0,
                    is_primary: true
                };
                setProductPhotos([primaryPhoto]);
            } else {
                setProductPhotos([]);
            }
            console.log('Using product primary photo from list:', product?.primary_photo);
            setError('Failed to load product photos: ' + (err.response?.data?.message || err.message || 'Unknown error'));
        } finally {
            setPhotosLoading(false);
        }
    };

    const openPhotoModal = async (product) => {
        console.log('Opening photo modal for product:', product);
        setSelectedProduct(product);
        setShowPhotoModal(true);
        setNewPhotos([]);
        setProductPhotos([]); // Clear previous photos
        await fetchProductPhotos(product.id);
    };

    const handleUploadPhotos = async () => {
        if (newPhotos.length === 0) return;

        try {
            setUploadingPhotos(true);
            const formDataObj = new FormData();

            // Add files to FormData - based on your API, use 'photos' field
            newPhotos.forEach(photo => {
                formDataObj.append('photos', photo);
            });

            // Add metadata if needed
            formDataObj.append('alt_text', `Photos for ${selectedProduct.name}`);

            console.log('Uploading photos:', newPhotos);

            await productsAPI.uploadPhotos(selectedProduct.id, formDataObj);

            // Refresh photos list
            await fetchProductPhotos(selectedProduct.id);
            setNewPhotos([]);
            setSuccess('Photos uploaded successfully!');

            // Refresh products list to update product photos
            fetchProducts();
        } catch (err) {
            console.error('Error uploading photos:', err);
            setError('Failed to upload photos: ' + (err.response?.data?.message || err.message || 'Unknown error'));
        } finally {
            setUploadingPhotos(false);
        }
    };

    const handleDeletePhoto = async (photoId) => {
        try {
            setDeletingPhotoId(photoId);
            await productsAPI.deletePhoto(selectedProduct.id, photoId);

            // Remove from local state
            setProductPhotos(prev => prev.filter(photo => photo.id !== photoId));
            setSuccess('Photo deleted successfully!');

            // Refresh products list
            fetchProducts();
        } catch (err) {
            console.error('Error deleting photo:', err);
            setError('Failed to delete photo: ' + (err.response?.data?.message || err.message || 'Unknown error'));
        } finally {
            setDeletingPhotoId(null);
        }
    };

    const handleSetPrimaryPhoto = async (photoId) => {
        try {
            setSettingPrimary(photoId);
            await productsAPI.setPrimaryPhoto(selectedProduct.id, photoId);

            // Update local state to mark this photo as primary
            setProductPhotos(prev => prev.map(photo => ({
                ...photo,
                is_primary: photo.id === photoId,
                position: photo.id === photoId ? 0 : (photo.position || 1)
            })));

            setSuccess('Primary photo updated successfully!');

            // Refresh products list to update primary photo
            fetchProducts();
        } catch (err) {
            console.error('Error setting primary photo:', err);
            setError('Failed to set primary photo: ' + (err.response?.data?.message || err.message || 'Unknown error'));
        } finally {
            setSettingPrimary(null);
        }
    };

    const handleNewPhotoChange = (e) => {
        const files = Array.from(e.target.files);
        setNewPhotos(files);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setError('');

            // Create FormData for API call with file upload
            const formDataObj = new FormData();

            // Add all form data
            formDataObj.append('vendor_id', formData.vendor_id);
            formDataObj.append('sku', formData.sku);
            formDataObj.append('name', formData.name);
            formDataObj.append('description', formData.description || '');
            formDataObj.append('category_id', formData.category_id);
            formDataObj.append('subcategory_id', formData.subcategory_id || '');
            formDataObj.append('is_active', formData.is_active);

            // Add photos if any
            photos.forEach(photo => {
                formDataObj.append('photos', photo);
            });

            console.log('Creating product with FormData:', formDataObj);

            const response = await productsAPI.create(formDataObj);

            console.log('Create product response:', response.data);

            setShowCreateModal(false);
            resetForm();
            setPhotos([]);
            setSuccess('Product created successfully!');

            // Refresh products list
            setTimeout(() => {
                fetchProducts();
            }, 500);
        } catch (err) {
            console.error('Error creating product:', err);
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to create product';
            setError(errorMessage);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setError('');

            // Check if there are new photos to upload
            if (photos.length > 0) {
                // Use FormData for file uploads
                const formDataObj = new FormData();

                // Append all form data
                formDataObj.append('vendor_id', formData.vendor_id);
                formDataObj.append('sku', formData.sku);
                formDataObj.append('name', formData.name);
                formDataObj.append('description', formData.description || '');
                formDataObj.append('category_id', formData.category_id);
                formDataObj.append('subcategory_id', formData.subcategory_id || '');
                formDataObj.append('is_active', formData.is_active);

                // Append new photos
                photos.forEach(photo => {
                    formDataObj.append('photos', photo);
                });

                await productsAPI.update(selectedProduct.id, formDataObj);
            } else {
                // Regular JSON update without new photos
                const updateData = {
                    vendor_id: formData.vendor_id,
                    sku: formData.sku,
                    name: formData.name,
                    description: formData.description || '',
                    category_id: formData.category_id,
                    subcategory_id: formData.subcategory_id || null,
                    is_active: parseInt(formData.is_active)
                };

                console.log('Updating product with data:', updateData);
                await productsAPI.update(selectedProduct.id, updateData);
            }

            setShowEditModal(false);
            resetForm();
            setSelectedProduct(null);
            setIsEditing(false);
            setPhotos([]);
            setSuccess('Product updated successfully!');

            // Refresh products list
            setTimeout(() => {
                fetchProducts();
            }, 500);
        } catch (err) {
            console.error('Error updating product:', err);
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to update product';
            setError(errorMessage);
        }
    };

    const handleDelete = async () => {
        try {
            setError('');
            await productsAPI.delete(selectedProduct.id);

            setShowDeleteModal(false);
            setSelectedProduct(null);
            setSuccess('Product deleted successfully!');

            // Refresh products list
            setTimeout(() => {
                fetchProducts();
            }, 500);
        } catch (err) {
            console.error('Error deleting product:', err);
            const errorMessage = err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to delete product';
            setError(errorMessage);
        }
    };

    const openEditModal = (product) => {
        console.log('Opening edit modal for product:', product);
        setSelectedProduct(product);
        setFormData({
            vendor_id: product.vendor_id || '',
            sku: product.sku || '',
            name: product.name || '',
            description: product.description || '',
            category_id: product.category_id || '',
            subcategory_id: product.subcategory_id || '',
            is_active: product.is_active ? '1' : '0'
        });
        setIsEditing(true);
        setShowEditModal(true);
        setPhotos([]);

        // If product has a category, fetch its subcategories
        if (product.category_id) {
            fetchSubcategories(product.category_id);
        }
    };

    const openDeleteModal = (product) => {
        setSelectedProduct(product);
        setShowDeleteModal(true);
    };

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);
        setPhotos(files);
    };

    const handleVendorChange = (vendorId) => {
        setFormData(prev => ({
            ...prev,
            vendor_id: vendorId
        }));
    };

    const handleCategoryChange = (categoryId) => {
        setFormData(prev => ({
            ...prev,
            category_id: categoryId,
            subcategory_id: '' // Reset subcategory when category changes
        }));
    };

    const handleSubcategoryChange = (subcategoryId) => {
        setFormData(prev => ({
            ...prev,
            subcategory_id: subcategoryId
        }));
    };

    const resetForm = () => {
        setFormData({
            vendor_id: '',
            sku: '',
            name: '',
            description: '',
            category_id: '',
            subcategory_id: '',
            is_active: '1'
        });
        setIsEditing(false);
        setSelectedProduct(null);
        setPhotos([]);
        setSubcategories([]);
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePhotoModalClose = () => {
        setShowPhotoModal(false);
        setProductPhotos([]);
        setNewPhotos([]);
        setSelectedProduct(null);
    };

    const handleVariantModalClose = () => {
        setShowVariantModal(false);
        setProductVariants([]);
        setSelectedProduct(null);
    };

    // Helper function to get image URL for product list
    const getImageUrl = (product) => {
        // Check for primary_photo from API response
        if (product.primary_photo && product.primary_photo.url) {
            return `${API_BASE_URL}/public/uploads/product_photos/${product.primary_photo.url}`;
        }

        // Check if product has photos array
        if (product.photos && Array.isArray(product.photos) && product.photos.length > 0) {
            const firstPhoto = product.photos[0];
            if (firstPhoto.url) {
                return `${API_BASE_URL}/public/uploads/product_photos/${firstPhoto.url}`;
            }
        }

        // Check for main_photo field
        if (product.main_photo) {
            return product.main_photo.startsWith('http') ? product.main_photo : `${API_BASE_URL}/public/uploads/product_photos/${product.main_photo}`;
        }

        // Check for thumbnail_url
        if (product.thumbnail_url) {
            return product.thumbnail_url.startsWith('http') ? product.thumbnail_url : `${API_BASE_URL}/public/uploads/product_photos/${product.thumbnail_url}`;
        }

        return null;
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(parseFloat(amount));
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">Loading products...</div>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Products Management</h1>
                    <p className="text-muted mb-0">Manage product catalog and inventory</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Create Product
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search products by name, SKU, description, vendor, or category..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end">
                            <small className="text-muted">
                                Showing {filteredProducts.length} of {products.length} products
                            </small>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th>Category</th>
                                <th>Vendor</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.length > 0 ? (
                                filteredProducts.map(product => (
                                    <tr key={product.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded me-3 d-flex align-items-center justify-content-center"
                                                    style={{ width: '50px', height: '50px', overflow: 'hidden' }}>
                                                    <Image
                                                        src={getImageUrl(product) || 'https://via.placeholder.com/50?text=No+Image'}
                                                        alt={product.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        rounded
                                                        onError={(e) => {
                                                            e.target.src = 'https://via.placeholder.com/50?text=No+Image';
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{product.name}</div>
                                                    <small className="text-muted">
                                                        {product.description?.substring(0, 50)}
                                                        {product.description?.length > 50 ? '...' : ''}
                                                    </small>
                                                    <br />
                                                    <small className="text-muted">ID: {product.id}</small>
                                                    {product.photo_count > 0 && (
                                                        <div>
                                                            <small className="text-info">
                                                                <FaImages className="me-1" size={10} />
                                                                {product.photo_count} photo(s)
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg="outline-secondary" text="dark" className="fw-normal">
                                                {product.sku || 'N/A'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="small fw-semibold">{product.category_name || product.category_id || 'Uncategorized'}</div>
                                                <div className="small text-muted">{product.subcategory_name || product.subcategory_id || 'No Subcategory'}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                {product.vendor_name || product.vendor_id || 'No Vendor'}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg={product.is_active ? 'success' : 'secondary'}>
                                                {product.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(product)}
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openPhotoModal(product)}
                                                >
                                                    <FaImages className="me-1" />
                                                    Photos
                                                </Button>
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openVariantModal(product)}
                                                >
                                                    <FaTags className="me-1" />
                                                    Variants
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(product)}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">
                                        {searchTerm ? 'No products found matching your search' : 'No products available'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Photo Management Modal */}
            <Modal show={showPhotoModal} onHide={handlePhotoModalClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <FaImages className="me-2 text-info" />
                        Manage Photos - {selectedProduct?.name}
                        {selectedProduct?.photo_count > 0 && (
                            <Badge bg="info" className="ms-2">
                                {selectedProduct?.photo_count} photos
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {photosLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading photos...</div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-3">
                                <Badge bg={productPhotos.length > 0 ? "info" : "secondary"}>
                                    {productPhotos.length} photo(s) found
                                </Badge>
                            </div>

                            {productPhotos.length > 0 ? (
                                <Row>
                                    {productPhotos.map((photo, index) => (
                                        <Col md={4} className="mb-3" key={photo.id || `photo-${index}`}>
                                            <div className="position-relative border rounded p-2">
                                                {/* Primary Photo Badge */}
                                                {photo.is_primary && (
                                                    <Badge bg="warning" className="position-absolute top-0 start-0 m-1">
                                                        <FaStar className="me-1" /> Primary
                                                    </Badge>
                                                )}

                                                <Image
                                                    src={photo.full_url || `${API_BASE_URL}/public/uploads/product_photos/${photo.url}`}
                                                    alt={photo.alt_text || 'Product photo'}
                                                    fluid
                                                    rounded
                                                    style={{
                                                        width: '100%',
                                                        height: '150px',
                                                        objectFit: 'cover',
                                                        backgroundColor: '#f8f9fa'
                                                    }}
                                                    onError={(e) => {
                                                        console.error('Image failed to load:', photo.full_url || photo.url);
                                                        e.target.src = 'https://via.placeholder.com/150?text=Image+Not+Found';
                                                        e.target.alt = 'Failed to load image';
                                                    }}
                                                />

                                                {/* Action Buttons */}
                                                <div className="position-absolute top-0 end-0 m-1 d-flex flex-column gap-1">
                                                    {!photo.is_primary && (
                                                        <Button
                                                            variant="warning"
                                                            size="sm"
                                                            onClick={() => handleSetPrimaryPhoto(photo.id)}
                                                            disabled={settingPrimary === photo.id}
                                                            style={{ width: '30px', height: '30px', padding: 0 }}
                                                            title="Set as primary"
                                                        >
                                                            {settingPrimary === photo.id ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (
                                                                <FaStar />
                                                            )}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDeletePhoto(photo.id)}
                                                        disabled={deletingPhotoId === photo.id}
                                                        style={{ width: '30px', height: '30px', padding: 0 }}
                                                        title="Delete photo"
                                                    >
                                                        {deletingPhotoId === photo.id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <FaTimes />
                                                        )}
                                                    </Button>
                                                </div>

                                                <div className="mt-2 small">
                                                    <div><strong>Alt Text:</strong> {photo.alt_text || 'No alt text'}</div>
                                                    <div><strong>Position:</strong> {photo.position}</div>
                                                    {photo.original_name && (
                                                        <div><strong>File:</strong> {photo.original_name}</div>
                                                    )}
                                                    {photo.file_size && (
                                                        <div><strong>Size:</strong> {formatFileSize(photo.file_size)}</div>
                                                    )}
                                                    {photo.mime_type && (
                                                        <div><strong>Type:</strong> {photo.mime_type}</div>
                                                    )}
                                                    {photo.created_at && (
                                                        <div><strong>Uploaded:</strong> {new Date(photo.created_at).toLocaleDateString()}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    <FaImages size={48} className="mb-3" />
                                    <div>No photos available for this product</div>
                                    <small>Product ID: {selectedProduct?.id}</small>
                                </div>
                            )}

                            <hr />

                            <div className="mt-3">
                                <h6 className="mb-3">
                                    <FaUpload className="me-2" />
                                    Upload More Photos
                                </h6>
                                <Form.Group>
                                    <Form.Control
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleNewPhotoChange}
                                    />
                                    <Form.Text className="text-muted">
                                        {newPhotos.length > 0
                                            ? `${newPhotos.length} file(s) selected`
                                            : 'Select multiple images to upload'
                                        }
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handlePhotoModalClose}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUploadPhotos}
                        disabled={newPhotos.length === 0 || uploadingPhotos || photosLoading}
                        className="d-flex align-items-center"
                    >
                        {uploadingPhotos ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <FaUpload className="me-2" />
                                Upload Photos
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Variant Management Modal */}
            <Modal show={showVariantModal} onHide={handleVariantModalClose} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <FaTags className="me-2 text-warning" />
                        Manage Variants - {selectedProduct?.name}
                        <Badge bg="info" className="ms-2">
                            {productVariants.length} variant(s)
                        </Badge>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {variantsLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading variants...</div>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <Badge bg={productVariants.length > 0 ? "info" : "secondary"}>
                                        {productVariants.length} variant(s) found
                                    </Badge>
                                </div>
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="d-flex align-items-center"
                                    onClick={openCreateVariantModal}
                                >
                                    <FaPlus className="me-1" />
                                    Create New Variant
                                </Button>
                            </div>

                            {productVariants.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Variant Title</th>
                                                <th>SKU</th>
                                                <th>Price</th>
                                                <th>Compare Price</th>
                                                <th>Weight</th>
                                                <th>Stock</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productVariants.map(variant => (
                                                <tr key={variant.id}>
                                                    <td>
                                                        <div className="fw-semibold">{variant.title || 'N/A'}</div>
                                                        <small className="text-muted">ID: {variant.id}</small>
                                                    </td>
                                                    <td>
                                                        <Badge bg="outline-secondary" text="dark" className="fw-normal">
                                                            {variant.sku || 'N/A'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <FaDollarSign className="me-1 text-success" size={12} />
                                                            <span className="fw-semibold">{formatCurrency(variant.price)}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {variant.compare_price ? (
                                                            <div className="d-flex align-items-center">
                                                                <FaDollarSign className="me-1 text-secondary" size={12} />
                                                                <span className="text-decoration-line-through text-muted">
                                                                    {formatCurrency(variant.compare_price)}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {variant.weight ? (
                                                            <div className="d-flex align-items-center">
                                                                <FaWeightHanging className="me-1 text-info" size={12} />
                                                                <span>{variant.weight} g</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <FaBoxOpen className="me-1 text-primary" size={12} />
                                                            <span>
                                                                <Badge bg={variant.available_stock > 0 ? "success" : "danger"}>
                                                                    {variant.available_stock} available
                                                                </Badge>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg={variant.in_stock ? "success" : "secondary"}>
                                                            {variant.in_stock ? 'In Stock' : 'Out of Stock'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="d-flex align-items-center"
                                                                onClick={() => openEditVariantModal(variant)}
                                                            >
                                                                <FaEdit className="me-1" />
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="d-flex align-items-center"
                                                                onClick={() => openDeleteVariantModal(variant)}
                                                            >
                                                                <FaTrash className="me-1" />
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    <FaTags size={48} className="mb-3" />
                                    <div>No variants available for this product</div>
                                    <small>Product ID: {selectedProduct?.id}</small>
                                    <div className="mt-3">
                                        <Button
                                            variant="success"
                                            className="d-flex align-items-center mx-auto"
                                            onClick={openCreateVariantModal}
                                        >
                                            <FaPlus className="me-2" />
                                            Create First Variant
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleVariantModalClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create Variant Modal */}
            <FormModal
                show={showCreateVariantModal}
                onHide={() => setShowCreateVariantModal(false)}
                title="Create New Variant"
                onSubmit={handleCreateVariant}
                submitText="Create Variant"
                size="lg"
            >
                <Form.Group className="mb-3">
                    <Form.Label>SKU *</Form.Label>
                    <Form.Control
                        type="text"
                        name="sku"
                        value={variantFormData.sku}
                        onChange={handleVariantFormChange}
                        required
                        placeholder="Enter variant SKU"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Variant Title *</Form.Label>
                    <Form.Control
                        type="text"
                        name="title"
                        value={variantFormData.title}
                        onChange={handleVariantFormChange}
                        required
                        placeholder="Enter variant title (e.g., Red Edition, 256GB)"
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Price *</Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                value={variantFormData.price}
                                onChange={handleVariantFormChange}
                                required
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Compare Price</Form.Label>
                            <Form.Control
                                type="number"
                                name="compare_price"
                                value={variantFormData.compare_price}
                                onChange={handleVariantFormChange}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                            />
                            <Form.Text className="text-muted">
                                Original price for showing discount
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Weight (grams)</Form.Label>
                    <Form.Control
                        type="number"
                        name="weight"
                        value={variantFormData.weight}
                        onChange={handleVariantFormChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                    />
                    <Form.Text className="text-muted">
                        Product weight in grams
                    </Form.Text>
                </Form.Group>

                {selectedProduct && (
                    <Form.Group className="mb-3">
                        <Form.Label>Product Info</Form.Label>
                        <div className="small text-muted">
                            <div><strong>Product:</strong> {selectedProduct.name}</div>
                            <div><strong>Product ID:</strong> {selectedProduct.id}</div>
                        </div>
                    </Form.Group>
                )}
            </FormModal>

            {/* Edit Variant Modal */}
            <FormModal
                show={showEditVariantModal}
                onHide={() => setShowEditVariantModal(false)}
                title="Edit Variant"
                onSubmit={handleUpdateVariant}
                submitText="Update Variant"
                size="lg"
            >
                <Form.Group className="mb-3">
                    <Form.Label>SKU *</Form.Label>
                    <Form.Control
                        type="text"
                        name="sku"
                        value={variantFormData.sku}
                        onChange={handleVariantFormChange}
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Variant Title *</Form.Label>
                    <Form.Control
                        type="text"
                        name="title"
                        value={variantFormData.title}
                        onChange={handleVariantFormChange}
                        required
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Price *</Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                value={variantFormData.price}
                                onChange={handleVariantFormChange}
                                required
                                step="0.01"
                                min="0"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Compare Price</Form.Label>
                            <Form.Control
                                type="number"
                                name="compare_price"
                                value={variantFormData.compare_price}
                                onChange={handleVariantFormChange}
                                step="0.01"
                                min="0"
                            />
                            <Form.Text className="text-muted">
                                Original price for showing discount
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Weight (grams)</Form.Label>
                    <Form.Control
                        type="number"
                        name="weight"
                        value={variantFormData.weight}
                        onChange={handleVariantFormChange}
                        step="0.01"
                        min="0"
                    />
                    <Form.Text className="text-muted">
                        Product weight in grams
                    </Form.Text>
                </Form.Group>

                {selectedProduct && selectedVariant && (
                    <Form.Group className="mb-3">
                        <Form.Label>Variant Info</Form.Label>
                        <div className="small text-muted">
                            <div><strong>Product:</strong> {selectedProduct.name}</div>
                            <div><strong>Variant ID:</strong> {selectedVariant.id}</div>
                            <div><strong>Created:</strong> {selectedVariant.created_at || 'Unknown'}</div>
                            <div><strong>Available Stock:</strong> {selectedVariant.available_stock || 0}</div>
                        </div>
                    </Form.Group>
                )}
            </FormModal>

            {/* Delete Variant Confirmation Modal */}
            <ConfirmModal
                show={showDeleteVariantModal}
                onHide={() => setShowDeleteVariantModal(false)}
                onConfirm={handleDeleteVariant}
                title="Delete Variant"
                message={`Are you sure you want to delete variant "${selectedVariant?.title}"? This will also remove associated inventory.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />

            {/* Rest of your existing modals (Create Product, Edit Product, Delete Product) */}
            {/* ... Existing modals remain unchanged ... */}
            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Product"
                onSubmit={handleCreate}
                submitText="Create Product"
                size="lg"
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter product name"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>SKU *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.sku}
                                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                required
                                placeholder="Enter SKU (e.g., GC-ELECTRIC-001)"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter product description"
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor *</Form.Label>
                            <Form.Select
                                value={formData.vendor_id}
                                onChange={(e) => handleVendorChange(e.target.value)}
                                required
                                disabled={vendorsLoading}
                            >
                                <option value="">Select Vendor</option>
                                {vendorsLoading ? (
                                    <option disabled>Loading vendors...</option>
                                ) : (
                                    vendors.map(vendor => (
                                        <option key={vendor.id} value={vendor.id}>
                                            {vendor.name} ({vendor.email || vendor.vendor_id})
                                        </option>
                                    ))
                                )}
                            </Form.Select>
                            {vendorsLoading && (
                                <div className="mt-1">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    <small className="text-muted">Loading vendors...</small>
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category *</Form.Label>
                            <Form.Select
                                value={formData.category_id}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                required
                                disabled={categoriesLoading}
                            >
                                <option value="">Select Category</option>
                                {categoriesLoading ? (
                                    <option disabled>Loading categories...</option>
                                ) : (
                                    categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))
                                )}
                            </Form.Select>
                            {categoriesLoading && (
                                <div className="mt-1">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    <small className="text-muted">Loading categories...</small>
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Subcategory</Form.Label>
                            <Form.Select
                                value={formData.subcategory_id}
                                onChange={(e) => handleSubcategoryChange(e.target.value)}
                                disabled={!formData.category_id || subcategoriesLoading || subcategories.length === 0}
                            >
                                <option value="">Select Subcategory</option>
                                {subcategoriesLoading ? (
                                    <option disabled>Loading subcategories...</option>
                                ) : (
                                    subcategories.map(subcategory => (
                                        <option key={subcategory.id} value={subcategory.id}>
                                            {subcategory.name}
                                        </option>
                                    ))
                                )}
                            </Form.Select>
                            {subcategoriesLoading && (
                                <div className="mt-1">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    <small className="text-muted">Loading subcategories...</small>
                                </div>
                            )}
                            {!formData.category_id ? (
                                <Form.Text className="text-muted">
                                    Please select a category first
                                </Form.Text>
                            ) : subcategories.length === 0 ? (
                                <Form.Text className="text-muted">
                                    No subcategories available for this category
                                </Form.Text>
                            ) : null}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Product Photos</Form.Label>
                    <Form.Control
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoChange}
                    />
                    <Form.Text className="text-muted">
                        {photos.length > 0
                            ? `${photos.length} file(s) selected`
                            : 'Upload product images (multiple files allowed)'
                        }
                    </Form.Text>
                </Form.Group>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title="Edit Product"
                onSubmit={handleUpdate}
                submitText="Update Product"
                size="lg"
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>SKU *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.sku}
                                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor *</Form.Label>
                            <Form.Select
                                value={formData.vendor_id}
                                onChange={(e) => handleVendorChange(e.target.value)}
                                required
                            >
                                <option value="">Select Vendor</option>
                                {vendors.map(vendor => (
                                    <option key={vendor.id} value={vendor.id}>
                                        {vendor.name} ({vendor.email || vendor.vendor_id})
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category *</Form.Label>
                            <Form.Select
                                value={formData.category_id}
                                onChange={(e) => handleCategoryChange(e.target.value)}
                                required
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Subcategory</Form.Label>
                            <Form.Select
                                value={formData.subcategory_id}
                                onChange={(e) => handleSubcategoryChange(e.target.value)}
                                disabled={!formData.category_id || subcategories.length === 0}
                            >
                                <option value="">Select Subcategory</option>
                                {subcategories.map(subcategory => (
                                    <option key={subcategory.id} value={subcategory.id}>
                                        {subcategory.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {!formData.category_id && (
                                <Form.Text className="text-muted">
                                    Please select a category first
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Add More Product Photos</Form.Label>
                    <Form.Control
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoChange}
                    />
                    <Form.Text className="text-muted">
                        {photos.length > 0
                            ? `${photos.length} new file(s) selected - these will be added to existing photos`
                            : 'Select new photos to add to existing ones (optional)'
                        }
                    </Form.Text>
                </Form.Group>

                {selectedProduct && (
                    <Form.Group className="mb-3">
                        <Form.Label>Current Product Info</Form.Label>
                        <div className="small text-muted">
                            <div>ID: {selectedProduct.id}</div>
                            <div>Created: {selectedProduct.created_at || 'Unknown'}</div>
                            <div>Last Updated: {selectedProduct.updated_at || 'Never'}</div>
                            {selectedProduct.primary_photo && (
                                <div>
                                    <strong>Primary Photo:</strong> {selectedProduct.primary_photo.original_name}
                                </div>
                            )}
                            {selectedProduct.photo_count > 0 && (
                                <div>
                                    <strong>Total Photos:</strong> {selectedProduct.photo_count}
                                </div>
                            )}
                        </div>
                    </Form.Group>
                )}
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Product"
                message={`Are you sure you want to delete product "${selectedProduct?.name}"? This will also remove associated variants and inventory.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};

export default Products;